import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { doc, getDoc, setDoc, collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
    User, Edit, Settings, BarChart3, Home
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Import componentele de profil
import ProfileHeader from '../components/ProfileHeader';
import ProfileEditor from '../components/ProfileEditor';
import SubscriptionSettings from '../components/SubscriptionSettings';
import ProfileResults from '../components/ProfileResults';

const ProfilePage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // State pentru profil
    const [profileData, setProfileData] = useState({
        name: '',
        age: '',
        sex: '',
        sexualOrientation: '',
        city: '',
        income: '',
        photos: [],
        visibility: {
            age: true,
            sex: true,
            sexualOrientation: true,
            city: true,
            income: false
        }
    });

    // State pentru subscription
    const [subscription, setSubscription] = useState({
        type: 'none',
        endDate: null,
        autoRenew: true
    });

    // State pentru setări
    const [settings, setSettings] = useState({
        emailNotifications: true,
        eventUpdates: true
    });

    // State pentru testele completate
    const [testAnswers, setTestAnswers] = useState({});

    // State UI
    const [activeTab, setActiveTab] = useState(
        searchParams.get('tab') || 'profile'
    );
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Load profile data
    useEffect(() => {
        const loadProfileData = async () => {
            if (!currentUser) {
                navigate('/login');
                return;
            }

            setLoading(true);
            try {
                const profileRef = doc(db, 'userProfiles', currentUser.uid);
                const profileSnap = await getDoc(profileRef);

                if (profileSnap.exists()) {
                    setProfileData(profileSnap.data());
                }

                const userResponsesRef = doc(db, 'userResponses', currentUser.uid);
                const userResponsesSnap = await getDoc(userResponsesRef);

                if (userResponsesSnap.exists()) {
                    const data = userResponsesSnap.data();

                    if (data.subscription) {
                        setSubscription(prev => ({
                            ...prev,
                            type: data.subscription
                        }));
                    }

                    setTestAnswers(data.answers || {});
                }

                const settingsRef = doc(db, 'userSettings', currentUser.uid);
                const settingsSnap = await getDoc(settingsRef);

                if (settingsSnap.exists()) {
                    setSettings(settingsSnap.data());
                }

                const subscriptionsRef = collection(db, 'customers', currentUser.uid, 'subscriptions');
                const unsubscribe = onSnapshot(subscriptionsRef, (snapshot) => {
                    snapshot.forEach((doc) => {
                        const subData = doc.data();
                        if (subData.status === 'active' || subData.status === 'trialing') {
                            setSubscription(prev => ({
                                ...prev,
                                endDate: subData.current_period_end?.toDate(),
                                autoRenew: !subData.cancel_at_period_end
                            }));
                        }
                    });
                });

                return () => unsubscribe();
            } catch (error) {
                console.error('Eroare la încărcarea profilului:', error);
            } finally {
                setLoading(false);
            }
        };

        loadProfileData();
    }, [currentUser, navigate]);

    const saveProfile = async (newProfileData) => {
        setSaving(true);
        try {
            const profileRef = doc(db, 'userProfiles', currentUser.uid);
            await setDoc(profileRef, newProfileData, { merge: true });
            setProfileData(newProfileData);
        } catch (error) {
            console.error('Eroare la salvarea profilului:', error);
            alert('Eroare la salvarea profilului. Te rugăm încearcă din nou.');
        } finally {
            setSaving(false);
        }
    };

    const tabs = [
        { id: 'profile', label: 'Profil', icon: User },
        { id: 'edit', label: 'Editează', icon: Edit },
        { id: 'settings', label: 'Abonament', icon: Settings },
        { id: 'results', label: 'Rezultate', icon: BarChart3 },
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600">Se încarcă profilul...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
            {/* Header */}
            <div className="bg-white shadow-md border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center text-slate-600 hover:text-slate-900 font-medium"
                        >
                            <Home size={20} className="mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">Acasă</span>
                        </button>
                        <h1 className="text-lg sm:text-2xl font-bold text-slate-900">Profilul Meu</h1>
                        <div className="w-10 sm:w-24"></div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs — responsive */}
            <div className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-2 sm:px-6">
                    <div className="flex">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-6 py-3 sm:py-4 font-medium text-xs sm:text-sm transition-all ${isActive
                                        ? 'text-blue-600 border-b-2 border-blue-600'
                                        : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                >
                                    <Icon size={18} />
                                    <span className="hidden xs:inline sm:inline">{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Mobile: arată numele tab-ului activ */}
            <div className="sm:hidden bg-slate-50 border-b border-slate-200 px-4 py-2">
                <p className="text-xs text-slate-500 text-center font-medium">
                    {tabs.find(t => t.id === activeTab)?.label}
                </p>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                {activeTab === 'profile' && (
                    <ProfileHeader
                        profileData={profileData}
                        subscription={subscription}
                        onEdit={() => setActiveTab('edit')}
                    />
                )}

                {activeTab === 'edit' && (
                    <ProfileEditor
                        profileData={profileData}
                        onSave={saveProfile}
                        saving={saving}
                        currentUser={currentUser}
                    />
                )}

                {activeTab === 'settings' && (
                    <SubscriptionSettings
                        subscription={subscription}
                        settings={settings}
                        onSaveSettings={async (newSettings) => {
                            setSettings(newSettings);
                            const settingsRef = doc(db, 'userSettings', currentUser.uid);
                            await setDoc(settingsRef, newSettings, { merge: true });
                        }}
                        currentUser={currentUser}
                    />
                )}

                {activeTab === 'results' && (
                    <ProfileResults
                        answers={testAnswers}
                        subscription={subscription.type}
                    />
                )}
            </div>
        </div>
    );
};

export default ProfilePage;